/* eslint-disable max-lines */
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { APP_CONTENT_WIDTH } from "src/constants/layout";

const messageFadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const thinkingPulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
`;

const spinKeyframes = keyframes`
  to { transform: rotate(360deg); }
`;

const heroFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const heroLogoReveal = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const sendButtonPop = keyframes`
  from {
    opacity: 0;
    transform: scale(0);
  }
  60% {
    opacity: 1;
    transform: scale(1.15);
  }
  to {
    transform: scale(1);
  }
`;

export const LeapChatContentPositioner = styled.div`
  label: LeapChatContentPositioner;
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
  position: absolute;
  padding-bottom: var(--app-bottom, 0px);
  inset: 0;
  --leap-chat-width: min(100%, ${APP_CONTENT_WIDTH}px);
  align-items: center;
`;

export const LeapChatContainer = styled(LeapChatContentPositioner)`
  label: LeapChatContainer;
`;

export const LeapChatViewport = styled.div`
  label: MessageListViewport;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0;
  width: 100%;
  padding-top: var(--app-top, 0px);
  min-width: 100%;
  display: flex;
  align-items: space-between;
  justify-content: space-between;
  flex-direction: column;
  position: relative;
`;

export const MessageListContent = styled.div`
  label: MessageListContent;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  flex: 1;
  padding: calc(var(--app-top, 0px) + 1.6rem) 1.4rem 0;
  width: var(--leap-chat-width);
  min-width: var(--leap-chat-width);
`;

export const MessageContainer = styled.div`
  label: MessageContainer;
  box-shadow: 0 0 0 rgba(0, 0, 0, 0);
  transition:
    background-color 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.15s ease;
  border-radius: 8px;
  cursor: default;

  &[data-last="assistant"] {
    min-height: calc(
      var(--assistant-response-placeholder-height, 0px) - var(--app-top, 0px)
    );

    &[data-escalated] {
      min-height: auto;
    }
  }

  &[data-msg-role="user"] {
    scroll-margin: calc(var(--app-top, 0px) + 4rem) 0 0;
  }

  &[data-long-pressed] {
    background-color: rgba(0, 0, 0, 0.04);
    box-shadow: 0 0 0 0.3rem rgba(0, 0, 0, 0.04);
  }
`;

export const AssistantMessageRoot = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.6rem 0;
  animation: ${messageFadeIn} 0.3s ease-out;
`;

export const AssistantAvatar = styled.div`
  flex-shrink: 0;
  width: 1.6em;
  height: 1.6em;
  min-width: 1.6em;
  border-radius: 50%;
  overflow: hidden;
  background-color: var(--color-cream, #fffcf3);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const AssistantMessageContent = styled.div`
  background: var(--color-white);
  border: 1px solid var(--color-white);
  box-shadow: var(--light-shadow);
  border-radius: 20px;
  padding: 1em 1em 0.8em;
  font-weight: 400;
  font-size: var(--font-size, 1rem);
  line-height: 1.4;
  letter-spacing: -0.02em;
  min-width: 0;
  width: fit-content;
  max-width: 100%;
  overflow: hidden;

  @supports (corner-shape: squircle) {
    corner-shape: squircle;
    border-radius: 45px;
  }

  &:has(.prompt-options) {
    width: 100%;
  }
`;

export const AssistantMessageBody = styled.div`
  min-width: 0;
  width: 100%;
`;

export const UserMessageRoot = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  flex-direction: column;
  padding: 0.6rem 0;
  animation: ${messageFadeIn} 0.25s ease-out;
`;

export const UserMessageContent = styled.div`
  label: UserMessageContent;
  background-color: var(--color-midmorning-blue);
  border: 1px solid var(--color-midmorning-blue);
  border-radius: 20px;
  padding: 1em 1em 0.8em;
  font-weight: 400;
  font-size: var(--font-size, 1rem);
  line-height: 1.4;
  letter-spacing: -0.02em;
  max-width: 85%;
  overflow: hidden;

  @supports (corner-shape: squircle) {
    corner-shape: squircle;
    border-radius: 45px;
  }

  &:has([data-message-files]) {
    width: 85%;
  }
`;

export const ThinkingText = styled.span`
  color: var(--color-charcoal-60, rgba(33, 33, 33, 0.6));
  font-size: 0.95em;
  font-style: italic;
  animation: ${thinkingPulse} 1.5s ease-in-out infinite;
`;

export const MessageFooter = styled.div`
  margin-top: 0.7em;
  font-size: 0.85em;
  color: var(--color-charcoal-60);
  font-weight: 400;
  letter-spacing: -0.02em;
  color: var(--Greys-Charcoal60, rgba(33, 33, 33, 0.6));
  display: flex;
  align-items: center;
  gap: 0.4em;

  .footer-time {
    &::before {
      content: " · ";
    }
  }

  &.user-footer {
    text-align: right;
    font-size: 0.8em;
    margin-top: 0.4em;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.2rem;

    .confirmation {
      display: flex;
      align-items: center;
      line-height: 1;
    }
  }
`;

export const FooterName = styled.span``;

export const FooterError = styled.span`
  color: var(--color-error-red, #d32f2f);
  font-weight: 500;
`;

export const FooterRetryLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: var(--color-error-red, #d32f2f);
  text-decoration: underline;
  cursor: pointer;
`;

export const EmptyChatHero = styled.div`
  label: EmptyChatHero;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1.2rem;
  width: var(--leap-chat-width);
  margin: 0 auto;
  height: 100%;
`;

export const HeroLogo = styled.img`
  width: 80px;
  height: 80px;
  margin-bottom: 1.2rem;
  animation: ${heroLogoReveal} 1s cubic-bezier(0.16, 1, 0.3, 1) both;
`;

export const HeroHeading = styled.h1`
  font-family: var(--font-family-serif);
  color: var(--Greys-Charcoal, #212121);
  text-align: center;
  font-size: 2rem;
  font-style: normal;
  font-weight: 300;
  line-height: 120%; /* 38.4px */
  letter-spacing: -0.48px;

  margin: 0;
  text-wrap: balance;
  animation: ${heroFadeIn} 1s cubic-bezier(0.16, 1, 0.3, 1) 0.3s both;
`;

export const AddAttachmentButton = styled.button`
  label: AddAttachmentButton;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  min-width: 2.75rem;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  padding: 0;
  background: var(--color-white, #ffffff);
  box-shadow: var(--light-shadow);
  color: var(--color-charcoal-80);
  margin-bottom: 0.4rem;

  &:hover:not(:disabled) {
    background: var(--color-gray-lighter, #e6e3db);
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

export const HeroInputCardWrapper = styled.div`
  flex: 1;
  min-width: 0;
  border-radius: 1.7rem;
  background: rgba(33, 33, 33, 0.12);
  padding: 1.5px;
  box-shadow: var(--light-shadow);
  position: relative;
  transition: box-shadow 0.3s ease-in-out;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: var(--gradient-sunrise);
    opacity: 0;
    transition: opacity 0.3s ease-in-out;
  }

  &:focus-within::before {
    opacity: 1;
  }

  &:focus-within {
    box-shadow:
      var(--light-shadow),
      0 0 0 #45311614,
      -5px 5px 16px #45311614,
      -20px 20px 29px #45311612,
      -46px 45px 39px #45311608,
      -82px 81px 46px #45311603;
  }
`;

export const HeroInputCard = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  background: var(--color-white, #ffffff);
  border-radius: calc(1.7rem - 1.5px);
  padding: 0.4rem;
  gap: 0.3rem;
`;

export const HeroInput = styled.textarea`
  flex: 1 1 0%;
  min-width: 0;
  max-height: 14em;
  border: none;
  padding: 0.5rem 0.6rem;
  font-size: 1.05rem;
  line-height: 1.45;
  font-family: inherit;
  background: transparent;
  resize: none;
  outline: none;
  word-break: break-word;
  overflow-y: auto;
  color: var(--color-charcoal);
  letter-spacing: -0.02em;

  &::placeholder {
    color: var(--color-charcoal-60, rgba(33, 33, 33, 0.6));
  }
`;

export const ComposerIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;
  transition:
    background 0.15s,
    color 0.15s;
  background: var(--color-cream-darker, #f2efe7);
  color: var(--color-charcoal-60);

  @media (pointer: coarse) {
    width: 2.75rem;
    height: 2.75rem;
  }

  &[data-active] {
    background: var(--color-charcoal, #212121);
    color: #fff;
  }

  &:hover:not(:disabled) {
    background: var(--color-gray-lighter, #e6e3db);
  }

  &[data-active]:hover:not(:disabled) {
    background: var(--color-charcoal, #212121);
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }

  svg {
    width: 16px;
    height: 16px;
  }

  @media (pointer: coarse) {
    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

export const SendButton = styled.button`
  label: SendButton;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;
  animation: ${sendButtonPop} 0.25s ease-out;
  transition:
    background 0.15s,
    color 0.15s;
  background: var(--color-highlight-blue);
  color: #fff;

  @media (pointer: coarse) {
    width: 2.75rem;
    height: 2.75rem;
  }

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }

  svg {
    width: 16px;
    height: 16px;
  }

  @media (pointer: coarse) {
    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

export const ComposerRow = styled.div`
  label: ComposerRow;
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  width: 100%;
  width: var(--leap-chat-width);
  padding: 0 1rem;
  margin: 0 auto;
`;

export const ComposerCardArea = styled.div`
  label: ComposerCardArea;
  z-index: 5;
  padding: 1rem 0 1rem;
  background: var(--color-cream-80);
  backdrop-filter: blur(12px);
  pointer-events: none;
  position: sticky;
  bottom: 0;
  margin: 0;
  margin-top: 1rem;

  > * {
    pointer-events: auto;
    margin: 0 auto;
  }
`;

// Archive view
export const ArchiveList = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  width: 100%;
  padding-top: 0.5rem;
  position: relative;
`;

export const ArchiveListSectionTitle = styled.h2`
  display: flex;
  padding: 0.6rem 1rem 0.7rem 1.6rem;
  margin: 0;
  color: var(--color-charcoal-60, rgba(33, 33, 33, 0.6));
  font-family: var(--font-family);
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 140%;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

export const ArchiveItem = styled.button`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  max-width: 100%;
  padding: 1.6rem 1.2rem;
  background: var(--color-white);
  border: none;
  border-radius: 12px;
  box-shadow: var(--light-shadow);
  cursor: pointer;
  text-align: left;
  --hl-left: var(--border);
  --hl-transf: scaleX(1);
  --cover-opacity: 1;
  --border: 0px;
  transition:
    box-shadow 0.15s ease-out,
    transform 0.15s ease,
    left 0.15s ease,
    right 0.15s ease,
    top 0.15s ease,
    bottom 0.15s ease;
  margin: 0 1.4rem 1rem;

  &:active {
    transform: scale(0.97);
    background-color: var(--color-cream-darker, #f2efe7);
  }

  &:hover {
    --border: 2px;
  }

  &[data-active] {
    --border: 2px;
    --cover-opacity: 0.65;
  }

  &[data-escalated] {
    --hl-left: 5.5px;
    --tl-transf: scaleX(0.6);
  }

  &:focus-visible {
    outline: 2px solid var(--color-sunrise-blue, #80aeff);
    outline-offset: -2px;
  }

  &::before,
  &::after {
    transition:
      transform 0.1s ease,
      left 0.1s ease,
      right 0.1s ease,
      top 0.1s ease,
      bottom 0.1s ease;
  }

  &::before {
    content: "";
    position: absolute;
    inset: var(--border);
    background: var(--color-white);
    --br: calc(12px - var(--border));
    opacity: var(--cover-opacity, 1);
    border-radius: var(--br);
    transition:
      opacity 0.15s ease,
      left 0.15s ease,
      right 0.15s ease,
      top 0.15s ease,
      bottom 0.15s ease;
  }

  .archive-item-title {
    font-size: 0.95rem;
    font-weight: 500;
    letter-spacing: -0.02em;
    color: var(--color-charcoal);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding-right: 1rem;
  }

  .archive-item-meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .archive-item-badge {
    color: var(--Accessible-Blue, #3973da);
    font-size: 0.75rem;
    font-family: var(--font-family-serif);
    font-weight: 700;
    line-height: 140%;
    letter-spacing: 0.2px;
    text-transform: uppercase;
    white-space: nowrap;
    padding: 0.15em 0.5em;
    border-radius: 4px;
    background: rgba(57, 115, 218, 0.12);
  }

  .archive-item-date {
    font-size: 0.8rem;
    letter-spacing: -0.02em;
    color: rgba(33, 33, 33, 0.5);
  }

  > * {
    position: relative;
    z-index: 1;
  }

  overflow: hidden;
  background: var(--gradient-sunrise);

  .archive-item-unread-dot {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 0.8rem;
    width: 0.7rem;
    aspect-ratio: 1;
    border-radius: 50%;
    background: var(--color-highlight-blue);
  }
`;

// Drawer
export const DRAWER_DESKTOP_BREAKPOINT = "800px";

export const DrawerRoot = styled.div`
  label: DrawerRoot;
  --drawer-width: min(320px, calc(100vw - 6rem));
  position: absolute;
  inset: 0;
  overflow: hidden;

  @media (min-width: ${DRAWER_DESKTOP_BREAKPOINT}) {
    display: flex;
    flex-direction: row;
  }
`;

export const DrawerPanel = styled.div`
  label: DrawerPanel;
  border-right: 0.5px solid var(--color-gray, #aaa7a0);
  --drawer-bg: var(--color-cream-darker, #f2efe7);
  background: var(--drawer-bg);
  position: absolute;
  top: 0;
  padding-top: var(--app-top, 0px);
  left: 0;
  bottom: var(--app-bottom, 0px);
  width: var(--drawer-width);
  z-index: 20;
  transform: translateX(-100%);
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 0 0 1rem;
  padding-top: var(--app-top, 0px);

  &:focus {
    outline: none;
  }

  &[data-open] {
    transform: translateX(0);
  }

  @media (min-width: ${DRAWER_DESKTOP_BREAKPOINT}) {
    position: relative;
    transform: none;
    width: var(--drawer-width);
    min-width: var(--drawer-width);
    z-index: auto;
    border-right: 1px solid rgba(33, 33, 33, 0.1);
    --drawer-bg: var(--Primary-Cream, #fffcf3);
  }
`;

export const DrawerBackdrop = styled.div`
  label: DrawerBackdrop;
  position: absolute;
  inset: 0;
  z-index: 15;
  background: var(--color-cream-80);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;

  &[data-open] {
    opacity: 1;
    pointer-events: auto;
  }

  @media (min-width: ${DRAWER_DESKTOP_BREAKPOINT}) {
    display: none;
  }
`;

export const DrawerShift = styled.div`
  label: DrawerShift;
  position: absolute;
  inset: 0;
  transition: transform 0.3s ease;

  &[data-open] {
    transform: translateX(var(--drawer-width));
  }

  @media (min-width: ${DRAWER_DESKTOP_BREAKPOINT}) {
    position: relative;
    flex: 1;
    min-width: 0;
    transform: none !important;
  }
`;

export const DrawerHeader = styled.div`
  label: DrawerHeader;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 0.8rem;
`;

export const DrawerTitle = styled.div`
  font-family: var(--font-family-serif);
  font-size: 1.1rem;
  font-weight: 300;
  line-height: 1.1;
  letter-spacing: -0.015em;
  color: var(--color-charcoal);
`;

export const ChatHeaderBar = styled.div`
  label: ChatHeaderBar;
  display: flex;
  position: absolute;
  inset: 0;
  bottom: auto;
  align-items: center;
  justify-content: space-between;
  padding-top: var(--app-top, 0px);
  position: sticky;
  top: 0;
  z-index: 10;
  border-bottom: 0.5px solid var(--color-gray-light, #e6e3db);
  background: var(--color-cream-80);
  backdrop-filter: blur(12px);
  padding: 0 1rem;

  @media (min-width: ${DRAWER_DESKTOP_BREAKPOINT}) {
    display: none;
  }
`;

// Message file attachments (inside sent bubbles)
// Bleeds to edges of UserMessageContent (offsets its 1em / 0.8em padding)
export const MessageFilesWrap = styled.div`
  label: MessageFilesWrap;
  margin: -1em -1em 0;
  padding: 0;
  overflow: hidden;

  &:last-child {
    margin-bottom: -0.8em;
    border-radius: 20px;
  }

  &:not(:last-child) {
    margin-bottom: 0.6em;
  }
`;

export const MessageFilesGrid = styled.div`
  label: MessageFilesGrid;
  display: grid;
  gap: 3px;

  &[data-count="1"] {
    grid-template-columns: 1fr;
  }

  &[data-count="2"] {
    grid-template-columns: 1fr 1fr;
  }

  &[data-count="3"] {
    grid-template-columns: 1fr 1fr;

    > *:first-child {
      grid-column: 1 / -1;
    }
  }

  &[data-count="4"] {
    grid-template-columns: 1fr 1fr;
  }
`;

export const MessageFileCell = styled.div`
  label: MessageFileCell;
  position: relative;
  border-radius: 4px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.15);
  cursor: pointer;

  /* All children are absolutely positioned so only aspect-ratio
     determines cell dimensions — no shift when images load */
  > * {
    position: absolute;
    inset: 0;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  [data-count="1"] > & {
    aspect-ratio: 3 / 2;
  }

  [data-count="2"] > & {
    aspect-ratio: 1;
  }

  [data-count="3"] > &:first-child {
    aspect-ratio: 2 / 1;
  }

  [data-count="3"] > &:not(:first-child) {
    aspect-ratio: 1;
  }

  [data-count="4"] > & {
    aspect-ratio: 1;
  }
`;

export const MessageFileSpinner = styled.div`
  label: MessageFileSpinner;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: "";
    width: 1.4rem;
    height: 1.4rem;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: rgba(255, 255, 255, 0.8);
    border-radius: 50%;
    animation: ${spinKeyframes} 0.6s linear infinite;
  }
`;

export const MessageFileFallback = styled.div`
  label: MessageFileFallback;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.5rem;
  color: inherit;

  svg {
    width: 1.5rem;
    height: 1.5rem;
    opacity: 0.5;
  }

  .fallback-name {
    font-size: 0.75rem;
    font-weight: 500;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }

  .fallback-size {
    font-size: 0.7rem;
    opacity: 0.6;
  }
`;

// Attachment previews
export const AttachmentPreviewRow = styled.div`
  label: AttachmentPreviewRow;
  display: flex;
  gap: 0.4rem;
  flex-basis: 100%;
  padding: 0.2rem 0.6rem 0;
  overflow-x: auto;
`;

export const AttachmentPreviewItem = styled.div`
  label: AttachmentPreviewItem;
  position: relative;
  width: 3rem;
  height: 3rem;
  min-width: 3rem;
  border-radius: 0.5rem;
  overflow: hidden;
  background: var(--color-cream-darker, #f2efe7);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const AttachmentFileIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: var(--color-charcoal-60, rgba(33, 33, 33, 0.6));

  svg {
    width: 1.2rem;
    height: 1.2rem;
  }
`;

export const AttachmentRemoveButton = styled.button`
  label: AttachmentRemoveButton;
  position: absolute;
  top: 0.15rem;
  right: 0.15rem;
  width: 1.1rem;
  height: 1.1rem;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  line-height: 1;
  font-size: 0.6rem;

  svg {
    width: 0.55rem;
    height: 0.55rem;
  }
`;

export const AttachmentErrorOverlay = styled.div`
  label: AttachmentErrorOverlay;
  position: absolute;
  inset: 0;
  background: rgba(211, 47, 47, 0.15);
  border: 2px solid var(--color-error-red, #d32f2f);
  border-radius: inherit;
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: "!";
    width: 1rem;
    height: 1rem;
    border-radius: 50%;
    background: var(--color-error-red, #d32f2f);
    color: #fff;
    font-size: 0.65rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }
`;

export const AttachmentSpinnerOverlay = styled.div`
  label: AttachmentSpinnerOverlay;
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: "";
    width: 1rem;
    height: 1rem;
    border: 2px solid var(--color-charcoal-60, rgba(33, 33, 33, 0.6));
    border-top-color: transparent;
    border-radius: 50%;
    animation: ${spinKeyframes} 0.6s linear infinite;
  }
`;

const promptFadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const InlinePromptRow = styled.div`
  label: InlinePromptRow;
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-top: 1px solid var(--color-gray-light, #e6e3db);
  margin: 1em -1em -0.8em;
  animation: ${promptFadeIn} 0.25s ease-out;
`;

export const PromptButton = styled.button<{
  variant?: "primary" | "secondary";
}>`
  label: PromptButton;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4em;
  padding: 0.9em 1em;
  border: none;
  background: transparent;
  cursor: pointer;
  font: inherit;
  font-size: 1em;
  font-weight: 500;
  letter-spacing: -0.02em;
  color: ${({ variant }) =>
    variant === "primary"
      ? "var(--color-forest-green, #2e7d32)"
      : "var(--color-error-red, #b71c1c)"};

  &:not(:first-child) {
    border-left: 1px solid var(--color-gray-light, #e6e3db);
  }

  &:active:not(:disabled) {
    background: rgba(0, 0, 0, 0.04);
  }

  &:disabled {
    cursor: default;
    opacity: 0.35;
  }

  &:disabled[data-selected] {
    opacity: 1;
    font-weight: 600;
  }

  svg {
    width: 1em;
    height: 1em;
  }
`;

export const EscalationBanner = styled.div`
  text-align: left;
  font-size: 0.9rem;
  margin: 1em 0 0;
  letter-spacing: 0.04em;
  padding: 0.75rem 1rem;
  text-wrap: pretty;
  border-radius: 20px;
  background: var(--Secondary-Dark-Cream, #f2efe7);

  @supports (corner-shape: squircle) {
    corner-shape: squircle;
    border-radius: 45px;
  }
`;

export const OfflineIndicator = styled.div`
  label: OfflineIndicator;
  text-align: center;
  font-size: 0.8rem;
  color: var(--color-charcoal-60, rgba(33, 33, 33, 0.6));
  padding: 0.4rem 0;
`;

export const TextLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--Accessible-Blue, #3973da);
  font-feature-settings:
    "liga" off,
    "clig" off;
  font-family: "STK Bureau";
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 140%;
  letter-spacing: -0.28px;
`;
